import { isEqual } from 'lodash';
import {
  ENTITY_ARRAYS,
  KEYED_OBJECTS,
  WHOLE_VALUES,
} from '../collab/diffEngine';

// Comparateur de deux blobs `content` au format stocké/REST (tel que renvoyé
// par l'API versions). Contrairement au diffEngine collab — qui compare par
// référence en s'appuyant sur le structural sharing d'immer — les deux contents
// proviennent ici de JSON désérialisés (références toujours distinctes) : la
// comparaison se fait par VALEUR (isEqual). Sert l'UI d'historique (« ce qui a
// changé entre deux versions ») et, à terme, la preview d'un undo.
//
// Résumé retourné :
// {
//   boxes / containers / columns / variableGroups / masks :
//     { added: [id...], removed: [id...], modified: [id...] },
//   variables / triggers :
//     { added: [key...], removed: [key...], modified: [key...] },
//   configuration / variableOrderArray : { changed: bool },
//   isEmpty: bool  // aucun changement détecté
// }

const indexById = (arr) => {
  const map = new Map();
  (arr ?? []).forEach((entity) => {
    if (entity && entity.id != null) map.set(String(entity.id), entity);
  });
  return map;
};

const diffEntityArray = (prev, next) => {
  const prevById = indexById(prev);
  const nextById = indexById(next);
  const added = [];
  const removed = [];
  const modified = [];

  nextById.forEach((entity, id) => {
    if (!prevById.has(id)) added.push(id);
    else if (!isEqual(prevById.get(id), entity)) modified.push(id);
  });
  prevById.forEach((_, id) => {
    if (!nextById.has(id)) removed.push(id);
  });

  return { added, removed, modified };
};

const diffKeyedObject = (prev, next) => {
  const prevObj = prev ?? {};
  const nextObj = next ?? {};
  const added = [];
  const removed = [];
  const modified = [];

  Object.keys(nextObj).forEach((key) => {
    if (!(key in prevObj)) added.push(key);
    else if (!isEqual(prevObj[key], nextObj[key])) modified.push(key);
  });
  Object.keys(prevObj).forEach((key) => {
    if (!(key in nextObj)) removed.push(key);
  });

  return { added, removed, modified };
};

const isGroupEmpty = (group) =>
  group.added.length === 0 &&
  group.removed.length === 0 &&
  group.modified.length === 0;

export const diffContent = (prevContent, nextContent) => {
  const prev = prevContent ?? {};
  const next = nextContent ?? {};
  const summary = {};
  let isEmpty = true;

  ENTITY_ARRAYS.forEach((key) => {
    const group = diffEntityArray(prev[key], next[key]);
    summary[key] = group;
    if (!isGroupEmpty(group)) isEmpty = false;
  });

  KEYED_OBJECTS.forEach((key) => {
    const group = diffKeyedObject(prev[key], next[key]);
    summary[key] = group;
    if (!isGroupEmpty(group)) isEmpty = false;
  });

  WHOLE_VALUES.forEach((key) => {
    const changed = !isEqual(prev[key], next[key]);
    summary[key] = { changed };
    if (changed) isEmpty = false;
  });

  summary.isEmpty = isEmpty;
  return summary;
};
